<script setup>
import { ref, watch, computed, onMounted } from 'vue';
import { Loader2 } from 'lucide-vue-next';
import SendIcon from '@/components/preview/icons/Send.vue';
import SendUpIcon from '@/components/preview/icons/SendUp.vue';
import { models } from '@/lib/gen';
import Tooltip2 from '@/components/Tooltip2.vue';

const props = defineProps({
  isLoading: Boolean,
  model: {
    type: String,
    default: '',
  },
  count: {
    type: Number,
    default: 1,
  },
});

const cacheKey = 'create:createModel';

const emit = defineEmits(['single', 'batch']);
const mode = ref(localStorage.getItem(cacheKey) || 'single'); // Default to single mode or saved value

// Watch mode changes and persist to localStorage
watch(mode, (newMode) => {
  localStorage.setItem(cacheKey, newMode);
});

// Initialize mode from localStorage on component mount
onMounted(() => {
  const savedMode = localStorage.getItem(cacheKey);
  if (savedMode) {
    mode.value = savedMode;
  }
});

function handlePromptCheck() {
  if (mode.value === 'single') {
    emit('single');
  } else {
    emit('batch');
  }
}

const credit = computed(() => {
  const model = props.model;
  const item = models.find((item) => model === item.value);
  return item?.creditsCost || 0;
});

const totalCredit = computed(() => {
  return props.count * credit.value;
});
</script>
<template>
  <Tooltip2 :text="$t('create.submitPrompt')">
    <button
      @click="handlePromptCheck"
      :disabled="isLoading"
      class="relative flex items-center justify-center size-[42px] disabled:from-black/40 disabled:to-black/20 disabled:cursor-not-allowed transition-all duration-300 hover:scale-110 active:scale-95 group"
    >
      <SendIcon
        class="absolute top-[1px] left-[-3px] size-[48px] transition-transform duration-300 group-hover:scale-105"
      />
      <Loader2 v-if="isLoading" class="animate-spin h-5 w-5 text-white" />
      <SendUpIcon
        v-else
        class="h-5 w-5 text-white translate-y-[0px] transition-transform duration-300 group-hover:translate-y-[-1px]"
      />

      <div
        class="absolute -top-1 -right-1 flex items-center justify-center bg-[#84ff79] rounded-full border border-[#111111]/10 w-5 h-5 transition-all duration-300 hover:scale-110 group-hover:bg-[#70ff65] group-hover:shadow-md"
      >
        <span
          class="text-xs text-[#111111] font-semibold transition-transform duration-300 group-hover:scale-110"
          >{{ totalCredit }}</span
        >
      </div>
    </button>
  </Tooltip2>
</template>
